import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

/**
 * A file viewer taking over the whole screen.
 *
 * Built on `Dialog` rather than a bare `fixed inset-0` div, because both things
 * that go wrong here are things the dialog primitive already solves:
 *
 *  - `position: fixed` resolves against the nearest TRANSFORMED ancestor, not
 *    the viewport, and `DialogContent` centres itself with
 *    `translate-x-[-50%]`. A plain overlay opened from a dialog — the inbound
 *    email modal, the 849 detail page — filled that dialog instead of the
 *    screen, and was clipped further by the `overflow-hidden` cards between.
 *  - Portalling out to `document.body` fixes the size but strands the controls
 *    OUTSIDE the host dialog's focus trap, so Tab cycles through the covered
 *    dialog behind and a keyboard user can never reach Close. Nesting a real
 *    dialog hands the focus scope over and gives it back on close.
 *
 * Escape, the backdrop, `aria-modal` and focus restoration come with it, which
 * is why there is no key handling left in the viewers themselves.
 */
export function FullscreenOverlay({
  title,
  actions,
  onClose,
  children,
}: {
  /** Names the surface for assistive technology, and shows in the header. */
  title: string;
  /** Viewer-specific controls, placed before the close button. */
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const gt = useGT();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        // Above the dialog this was opened from, which sits on the shared
        // dialog layer. Inline rather than a `z-[2000]` class on purpose:
        // Handsontable builds selectors from ancestor class names, and a
        // bracketed one on a portalled ancestor threw `SyntaxError: not a valid
        // selector` and took the AS/400 grid down (#6211) — and a grid is
        // exactly what renders inside this overlay.
        style={{ zIndex: 2000 }}
        // Undo the centred, rounded, padded card: this one is the whole screen.
        className="top-0 left-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:max-w-none"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <DialogTitle className="text-base font-medium truncate">
            {title}
          </DialogTitle>
          <div className="flex items-center gap-2 shrink-0">
            {actions}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={onClose}
              aria-label={gt('Exit fullscreen', {
                context:
                  'accessible name for the close button on a fullscreen file viewer',
              })}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
        {children}
      </DialogContent>
    </Dialog>
  );
}
